import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

(() => {
  const ROOT_SELECTOR = '[data-epp-root]';
  const PAGE_CLASS = 'editorial-product-page__document';

  const pinPageInlineOrigin = () => {
    if (!document.documentElement.classList.contains(PAGE_CLASS)) return;
    const scroller = document.scrollingElement;
    if (scroller && scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
    if (document.body && document.body.scrollLeft !== 0) document.body.scrollLeft = 0;
  };

  const syncPageContainment = () => {
    const isEditorialProductPage = Boolean(document.querySelector('.editorial-product-page--hero'));
    document.documentElement.classList.toggle(PAGE_CLASS, isEditorialProductPage);
    document.body?.classList.toggle(PAGE_CLASS, isEditorialProductPage);
    if (isEditorialProductPage) window.requestAnimationFrame(pinPageInlineOrigin);
  };

  const money = (cents, format) => {
    const value = Number(cents || 0);
    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = Math.round(value / 100).toString();
    const amountWithComma = amount.replace('.', ',');
    const template = format || '${{amount}}';
    const formatted = template
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
    const parser = document.createElement('span');
    parser.innerHTML = formatted;
    return parser.textContent || formatted;
  };

  let conversionFrame;
  const queueCurrencyConversion = () => {
    if (conversionFrame) return;
    conversionFrame = window.requestAnimationFrame(() => {
      conversionFrame = undefined;
      try {
        window.bucksCC?.reConvert?.();
      } catch (error) {
        console.warn('[editorial-product] Currency refresh unavailable', error);
      }
    });
  };

  const setMoney = (element, cents, format, prefix = '') => {
    if (!element) return;
    if (cents == null) {
      element.replaceChildren(document.createTextNode(prefix));
      return;
    }
    const price = document.createElement('span');
    price.className = 'money';
    price.textContent = money(cents, format);
    element.replaceChildren(document.createTextNode(prefix), price);
    queueCurrencyConversion();
  };

  const setVariantImage = (image, source) => {
    const sized = (width) => {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    };
    const isPair = Boolean(image.closest('[data-epp-pair]'));
    const widths = isPair ? [96, 160, 240, 320] : [240, 360, 480, 640, 800, 1000];
    image.sizes = image.sizes || (isPair ? '90px' : '(max-width: 800px) 78vw, 25vw');
    image.srcset = widths.map((width) => `${sized(width)} ${width}w`).join(', ');
    image.src = sized(isPair ? 240 : 640);
    image.dataset.variantImage = source;
  };

  const replaceTokens = (template, values) => Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, value == null ? '' : String(value)),
    template || ''
  );

  const setCurrentMediaDot = (root, mediaId) => {
    const currentId = String(mediaId || '');
    root.querySelectorAll('[data-epp-dot]').forEach((dot) => {
      dot.setAttribute('aria-current', String(dot.dataset.mediaId === currentId));
    });
  };

  const promoteMedia = (root, mediaId, behavior = 'smooth') => {
    if (!mediaId) return false;
    const gallery = root.querySelector('[data-epp-gallery]');
    if (!gallery) return false;

    const currentId = String(mediaId);
    const target = [...gallery.querySelectorAll('[data-epp-media]')].find(
      (media) => media.dataset.mediaId === currentId
    );
    if (!target) return false;

    setCurrentMediaDot(root, currentId);

    const isMobileRail = window.matchMedia('(max-width: 800px)').matches;
    const mediaItems = [...gallery.querySelectorAll('[data-epp-media]')];
    const targetIndex = mediaItems.indexOf(target);

    if (!isMobileRail && gallery.firstElementChild !== target) {
      gallery.insertBefore(target, gallery.firstElementChild);
    }

    window.requestAnimationFrame(() => {
      const targetLeft = isMobileRail ? Math.max(0, targetIndex) * gallery.clientWidth : 0;
      if (typeof gallery.scrollTo === 'function') {
        gallery.scrollTo({ left: targetLeft, top: 0, behavior });
      } else {
        gallery.scrollLeft = targetLeft;
      }
      pinPageInlineOrigin();
    });
    return true;
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-epp-gallery]');
    const dots = [...root.querySelectorAll('[data-epp-dot]')];
    if (!gallery || dots.length < 2) return;

    let frame;
    const update = () => {
      frame = undefined;
      const mediaItems = [...gallery.querySelectorAll('[data-epp-media]')];
      if (!mediaItems.length) return;
      const current = mediaItems.reduce((closest, item) => (
        Math.abs(item.offsetLeft - gallery.scrollLeft) < Math.abs(closest.offsetLeft - gallery.scrollLeft)
          ? item
          : closest
      ));
      setCurrentMediaDot(root, current.dataset.mediaId);
    };

    gallery.addEventListener('scroll', () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    }, { passive: true });

    dots.forEach((dot) => dot.addEventListener('click', () => {
      promoteMedia(root, dot.dataset.mediaId, 'smooth');
    }));
  };

  const initModal = (root) => {
    const modal = root.querySelector('[data-epp-size-modal]');
    const openers = [...root.querySelectorAll('[data-epp-open-size]')];
    if (!modal || !openers.length) return;

    let opener;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const close = () => {
      modal.hidden = true;
      document.body.classList.remove('editorial-product-page__modal-open');
      if (opener && document.contains(opener)) opener.focus();
    };

    const open = (button) => {
      opener = button;
      modal.hidden = false;
      document.body.classList.add('editorial-product-page__modal-open');
      const first = modal.querySelector(focusableSelector);
      if (first) first.focus();
    };

    openers.forEach((button) => button.addEventListener('click', () => open(button)));
    modal.querySelectorAll('[data-epp-close-modal]').forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...modal.querySelectorAll(focusableSelector)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initQuantity = (root) => {
    const input = root.querySelector('[data-epp-quantity]');
    if (!input) return;

    const normalize = (value) => {
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step || 1);
      const parsed = Number(value);
      const safe = Number.isFinite(parsed) ? parsed : min;
      return Math.max(min, Math.min(max, Math.round(safe / step) * step));
    };

    root.querySelectorAll('[data-epp-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(input.step || 1);
        const direction = Number(button.dataset.eppQuantityChange || 0);
        input.value = normalize(Number(input.value || input.min || 1) + direction * step);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    input.addEventListener('change', () => { input.value = normalize(input.value); });
  };

  const parseJSON = (element) => {
    if (!element) return [];
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[editorial-product] Unable to read product variants', error);
      return [];
    }
  };

  const openCartDrawer = async (cartState) => {
    const currentDrawer = document.querySelector('theme-drawer#cart-drawer');
    if (currentDrawer) {
      await customElements.whenDefined('theme-drawer');
      if (cartState) {
        const drawerHTML = cartState.sections?.['cart-drawer-section'];
        if (drawerHTML) {
          await morphSection('cart-drawer-section', drawerHTML, { mode: 'hydration', injectStylesheet: true });
        } else {
          await sectionRenderer.renderSection('cart-drawer-section', {
            cache: false,
            mode: 'hydration',
            injectStylesheet: true,
          });
        }
        const cartItems = currentDrawer.querySelector('cart-items-component');
        const source = cartItems || currentDrawer;
        source.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: cartState.items.map((item) => ({ id: item.key, quantity: item.quantity })),
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartState),
            detail: { items: cartState.items, itemCount: cartState.item_count, sections: cartState.sections, source: 'editorial-product', didError: false },
          }),
        }));
      }
      if (typeof currentDrawer.open === 'function' && !currentDrawer.isOpen) currentDrawer.open();
      return;
    }
    const atlasDrawer = document.querySelector('cart-drawer');
    if (atlasDrawer) {
      await customElements.whenDefined('cart-drawer');
      if (cartState?.sections && typeof atlasDrawer.renderContents === 'function') {
        atlasDrawer.renderContents(cartState);
        return;
      }
      if (typeof atlasDrawer.open === 'function') {
        atlasDrawer.open();
        return;
      }
    }

    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    await customElements.whenDefined('theme-drawer');
    if (typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  };

  const responseJSON = async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || `Cart request failed (${response.status})`);
    }
    return payload;
  };

  const consolidateItems = (items) => {
    const consolidated = new Map();
    items.forEach((item) => {
      const properties = item.properties || {};
      const key = `${item.id}:${JSON.stringify(properties)}`;
      const current = consolidated.get(key);
      if (current) {
        current.quantity += Number(item.quantity || 1);
      } else {
        consolidated.set(key, { ...item, quantity: Number(item.quantity || 1), properties });
      }
    });
    return [...consolidated.values()];
  };

  const addCartItems = async (items, discountCode = '') => {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const sections = document.querySelector('theme-drawer#cart-drawer')
      ? ['cart-drawer-section']
      : ['atlas-cart-drawer', 'atlas-cart-icon-bubble'];
    const addResponse = await fetch(`${routeRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: consolidateItems(items), sections, sections_url: location.pathname })
    });
    let cartState = await responseJSON(addResponse);
    let discountApplied = !discountCode;

    if (discountCode) {
      try {
        const discountResponse = await fetch(`${routeRoot}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: discountCode, sections, sections_url: location.pathname })
        });
        cartState = await responseJSON(discountResponse);
        const matchingCode = (cartState.discount_codes || []).find(
          (discount) => String(discount.code || '').toLowerCase() === discountCode.toLowerCase()
        );
        discountApplied = Boolean(matchingCode && matchingCode.applicable !== false);
      } catch (error) {
        discountApplied = false;
        console.warn('[editorial-product] Duo discount code was not applied', error);
      }
    }

    if (document.querySelector('theme-drawer#cart-drawer') && typeof cartState.item_count !== 'number') {
      const renderedSections = cartState.sections;
      const currentCartResponse = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      cartState = await responseJSON(currentCartResponse);
      cartState.sections = renderedSections;
    }
    await openCartDrawer(cartState);
    return { cartState, discountApplied };
  };

  const initCartDrawer = (root) => {
    root.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.action !== 'add') return;

      const openAfterUpdate = ({ detail } = {}) => {
        if (detail?.didError) return;
        window.setTimeout(() => {
          openCartDrawer().catch((error) => console.warn('[editorial-product] Unable to open cart drawer', error));
        }, 0);
      };

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(openAfterUpdate).catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[editorial-product] Cart update failed', error);
        });
      } else {
        openAfterUpdate();
      }
    });
  };

  const initBundle = (root) => {
    const bundle = root.querySelector('[data-epp-bundle]');
    const form = root.querySelector('[data-epp-product-form]');
    const data = root.querySelector('[data-epp-product-json]');
    const addButton = form?.querySelector('[data-epp-add]');
    if (!bundle || !form || !addButton || !data) return;

    const variants = parseJSON(data);
    if (!variants.length) return;

    const modeInputs = [...bundle.querySelectorAll('[data-epp-bundle-mode]')];
    const cards = [...bundle.querySelectorAll('[data-epp-bundle-card]')];
    const duoOptions = bundle.querySelector('[data-epp-duo-options]');
    const itemElements = [...bundle.querySelectorAll('[data-epp-bundle-item]')];
    const singlePrice = bundle.querySelector('[data-epp-bundle-single-price]');
    const singleCompare = bundle.querySelector('[data-epp-bundle-single-compare]');
    const duoPrice = bundle.querySelector('[data-epp-bundle-duo-price]');
    const duoCompare = bundle.querySelector('[data-epp-bundle-duo-compare]');
    const duoShipping = bundle.querySelector('[data-epp-duo-shipping]');
    const errorElement = bundle.querySelector('[data-epp-bundle-error]');
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const showButtonPrice = root.dataset.showButtonPrice === 'true';
    const discountCode = (bundle.dataset.discountCode || '').trim();
    const discountPercent = Math.max(0, Math.min(100, Number(bundle.dataset.discountPercent || 0)));
    const hasDiscount = Boolean(discountCode && discountPercent > 0);
    let currentVariant = variants.find((variant) => String(variant.id) === String(form.querySelector('[data-epp-variant-id]')?.value)) || variants[0];
    let duoTouched = false;
    let busy = false;

    const mode = () => modeInputs.find((input) => input.checked)?.value || 'single';

    const setError = (message = '') => {
      if (!errorElement) return;
      errorElement.hidden = !message;
      errorElement.textContent = message;
    };

    const selectsForItem = (item) => [...item.querySelectorAll('[data-epp-bundle-option]')];

    const selectionsForItem = (item) => {
      const selected = [];
      selectsForItem(item).forEach((select) => {
        selected[Number(select.dataset.optionPosition) - 1] = select.value;
      });
      return selected;
    };

    const variantForItem = (item) => {
      const selected = selectionsForItem(item);
      if (!selected.length) return variants.find((variant) => variant.available) || variants[0];
      return variants.find((variant) => (
        variant.options.length === selected.length &&
        variant.options.every((option, index) => option === selected[index])
      ));
    };

    const updateItemAvailability = (item) => {
      const selected = selectionsForItem(item);
      selectsForItem(item).forEach((select) => {
        const position = Number(select.dataset.optionPosition) - 1;
        [...select.options].forEach((optionElement) => {
          const possible = variants.some((variant) => variant.available && variant.options.every((option, index) => (
            index === position ? option === optionElement.value : !selected[index] || option === selected[index]
          )));
          optionElement.disabled = !possible && optionElement.value !== select.value;
        });
      });
    };

    const syncDuoToVariant = (variant) => {
      if (!variant) return;
      itemElements.forEach((item) => {
        selectsForItem(item).forEach((select) => {
          const value = variant.options[Number(select.dataset.optionPosition) - 1];
          if ([...select.options].some((option) => option.value === value)) select.value = value;
        });
        updateItemAvailability(item);
      });
    };

    const updateSingleCard = () => {
      if (!currentVariant) return;
      if (singlePrice) setMoney(singlePrice, currentVariant.price, moneyFormat);
      const onSale = Number(currentVariant.compare_at_price || 0) > Number(currentVariant.price || 0);
      if (singleCompare) {
        singleCompare.hidden = !onSale;
        setMoney(singleCompare, onSale ? currentVariant.compare_at_price : null, moneyFormat);
      }
    };

    const updateSingleButton = () => {
      const available = Boolean(currentVariant?.available);
      addButton.disabled = busy || !available;
      const label = !currentVariant
        ? addButton.dataset.unavailableLabel
        : available
          ? addButton.dataset.availableLabel
          : addButton.dataset.soldOutLabel;
      setMoney(addButton, available && showButtonPrice ? currentVariant.price : null, moneyFormat,
        available && showButtonPrice ? `${label} · ` : label);
    };

    const update = () => {
      const selectedMode = mode();
      root.dataset.bundleMode = selectedMode;
      addButton.type = selectedMode === 'duo' ? 'button' : 'submit';
      cards.forEach((card) => card.classList.toggle('is-selected', card.dataset.eppBundleCard === selectedMode));
      if (duoOptions) duoOptions.hidden = selectedMode !== 'duo';
      updateSingleCard();

      const chosenVariants = itemElements.map(variantForItem);
      const allAvailable = chosenVariants.length === 2 && chosenVariants.every((variant) => variant?.available);
      const regularTotal = chosenVariants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
      const displayTotal = hasDiscount
        ? Math.round(regularTotal * (100 - discountPercent) / 100)
        : regularTotal;
      const compareTotal = chosenVariants.reduce((sum, variant) => {
        const compareAt = Number(variant?.compare_at_price || 0);
        const price = Number(variant?.price || 0);
        return sum + (compareAt > price ? compareAt : price);
      }, 0);
      const referenceTotal = compareTotal > displayTotal
        ? compareTotal
        : hasDiscount && regularTotal > displayTotal
          ? regularTotal
          : 0;

      if (duoPrice) setMoney(duoPrice, displayTotal, moneyFormat);
      if (duoCompare) {
        duoCompare.hidden = referenceTotal <= displayTotal;
        setMoney(duoCompare, referenceTotal > displayTotal ? referenceTotal : null, moneyFormat);
      }

      if (duoShipping) duoShipping.hidden = !(allAvailable && displayTotal > 10000 && bundle.dataset.shippingCurrency === 'USD');

      if (selectedMode === 'single') {
        updateSingleButton();
        return;
      }

      addButton.disabled = busy || !allAvailable;
      const label = allAvailable ? bundle.dataset.duoAddLabel : addButton.dataset.unavailableLabel;
      setMoney(addButton, allAvailable && showButtonPrice ? displayTotal : null, moneyFormat,
        allAvailable && showButtonPrice ? `${label} · ` : label);
    };

    modeInputs.forEach((input) => input.addEventListener('change', () => {
      setError();
      update();
    }));

    itemElements.forEach((item) => {
      selectsForItem(item).forEach((select) => select.addEventListener('change', () => {
        duoTouched = true;
        setError();
        updateItemAvailability(item);
        update();
      }));
    });

    root.addEventListener('editorial:variant-change', (event) => {
      currentVariant = event.detail?.variant || null;
      if (mode() === 'single' || !duoTouched) syncDuoToVariant(currentVariant);
      update();
    });

    const addDuo = async () => {
      if (busy) return;

      const chosenVariants = itemElements.map(variantForItem);
      if (chosenVariants.length !== 2 || chosenVariants.some((variant) => !variant?.available)) {
        setError(bundle.dataset.errorMessage);
        update();
        return;
      }

      busy = true;
      setError();
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.textContent = bundle.dataset.loadingLabel;

      try {
        const result = await addCartItems(chosenVariants.map((variant) => ({
          id: variant.id,
          quantity: 1,
          properties: { _editorial_bundle: 'duo' }
        })), discountCode);
        if (discountCode && !result.discountApplied) setError(bundle.dataset.discountErrorMessage);
      } catch (error) {
        setError(error.message || bundle.dataset.errorMessage);
      } finally {
        busy = false;
        addButton.removeAttribute('aria-busy');
        update();
      }
    };

    addButton.addEventListener('click', (event) => {
      if (mode() !== 'duo') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addDuo();
    }, true);

    form.addEventListener('submit', (event) => {
      if (mode() !== 'duo') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addDuo();
    }, true);

    syncDuoToVariant(currentVariant);
    update();
  };

  const initPairs = (root) => {
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    root.querySelectorAll('[data-epp-pair]').forEach((pair) => {
      const variants = parseJSON(pair.querySelector('[data-epp-pair-json]'));
      const selects = [...pair.querySelectorAll('[data-epp-pair-option]')];
      const picker = pair.querySelector('[data-epp-pair-picker]');
      const button = pair.querySelector('[data-epp-pair-action]');
      const price = pair.querySelector('[data-epp-pair-price]');
      const compare = pair.querySelector('[data-epp-pair-compare]');
      const saving = pair.querySelector('[data-epp-pair-saving]');
      const image = pair.querySelector('.editorial-product-page__pair-image img');
      const errorElement = pair.querySelector('[data-epp-pair-error]');
      const defaultOnly = pair.dataset.defaultOnly === 'true';
      if (!variants.length || !button) return;

      let busy = false;

      const setError = (message = '') => {
        if (!errorElement) return;
        errorElement.hidden = !message;
        errorElement.textContent = message;
      };

      const selections = () => {
        const selected = [];
        selects.forEach((select) => {
          selected[Number(select.dataset.optionPosition) - 1] = select.value;
        });
        return selected;
      };

      const selectedVariant = () => {
        const selected = selections();
        if (!selected.length) return variants.find((variant) => variant.available) || variants[0];
        return variants.find((variant) => (
          variant.options.length === selected.length &&
          variant.options.every((option, index) => option === selected[index])
        ));
      };

      const updateAvailability = () => {
        const selected = selections();
        selects.forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          [...select.options].forEach((optionElement) => {
            const possible = variants.some((variant) => variant.available && variant.options.every((option, index) => (
              index === position ? option === optionElement.value : !selected[index] || option === selected[index]
            )));
            optionElement.disabled = !possible && optionElement.value !== select.value;
          });
        });
      };

      const update = () => {
        const variant = selectedVariant();
        const available = Boolean(variant?.available);
        const onSale = Number(variant?.compare_at_price || 0) > Number(variant?.price || 0);
        if (price) setMoney(price, variant?.price || 0, moneyFormat);
        if (compare) {
          compare.hidden = !onSale;
          setMoney(compare, onSale ? variant.compare_at_price : null, moneyFormat);
        }
        if (saving) {
          saving.hidden = !onSale;
          setMoney(saving, onSale ? Number(variant.compare_at_price) - Number(variant.price) : null,
            moneyFormat, onSale ? `${pair.dataset.savingsPrefix} ` : '');
        }
        if (image && variant?.image && image.dataset.variantImage !== variant.image) {
          setVariantImage(image, variant.image);
        }
        button.disabled = busy || !available;
        if (!busy) button.textContent = !defaultOnly && picker && !picker.hidden
          ? button.dataset.confirmLabel
          : button.dataset.addLabel;
      };

      selects.forEach((select) => select.addEventListener('change', () => {
        setError();
        updateAvailability();
        update();
      }));

      button.addEventListener('click', async () => {
        if (!defaultOnly && picker?.hidden) {
          picker.hidden = false;
          button.setAttribute('aria-expanded', 'true');
          updateAvailability();
          update();
          selects[0]?.focus();
          return;
        }

        const variant = selectedVariant();
        if (!variant?.available || busy) {
          setError(pair.dataset.errorMessage);
          return;
        }

        busy = true;
        setError();
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel;
        try {
          await addCartItems([{
            id: variant.id,
            quantity: 1,
            properties: { _editorial_offer: 'pairs-well' }
          }]);
          if (picker) picker.hidden = true;
          button.setAttribute('aria-expanded', 'false');
        } catch (error) {
          setError(error.message || pair.dataset.errorMessage);
        } finally {
          busy = false;
          button.removeAttribute('aria-busy');
          update();
        }
      });

      updateAvailability();
      update();
    });
  };

  const initRecommendations = (root) => {
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';

    root.querySelectorAll('[data-epp-recommendation]').forEach((card) => {
      const variants = parseJSON(card.querySelector('[data-epp-recommendation-json]'));
      const selects = [...card.querySelectorAll('[data-epp-rec-option]')];
      const swatches = [...card.querySelectorAll('[data-epp-rec-swatch]')];
      const picker = card.querySelector('[data-epp-rec-picker]');
      const button = card.querySelector('[data-epp-rec-action]');
      const image = card.querySelector('.editorial-product-page__recommendation-image--primary');
      const price = card.querySelector('[data-epp-rec-price]');
      const compare = card.querySelector('[data-epp-rec-compare]');
      const saving = card.querySelector('[data-epp-rec-saving]');
      const errorElement = card.querySelector('[data-epp-rec-error]');
      const defaultOnly = card.dataset.defaultOnly === 'true';
      if (!variants.length) return;

      let busy = false;

      const setError = (message = '') => {
        if (!errorElement) return;
        errorElement.hidden = !message;
        errorElement.textContent = message;
      };

      const selections = () => {
        const selected = [];
        selects.forEach((select) => {
          selected[Number(select.dataset.optionPosition) - 1] = select.value;
        });
        return selected;
      };

      const selectedVariant = () => {
        const selected = selections();
        if (!selected.length) return variants.find((variant) => variant.available) || variants[0];
        return variants.find((variant) => (
          variant.options.length === selected.length &&
          variant.options.every((option, index) => option === selected[index])
        ));
      };

      const updateAvailability = () => {
        const selected = selections();

        selects.forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          [...select.options].forEach((optionElement) => {
            const possible = variants.some((variant) => variant.available && variant.options.every((option, index) => (
              index === position ? option === optionElement.value : !selected[index] || option === selected[index]
            )));
            optionElement.disabled = !possible && optionElement.value !== select.value;
          });
        });

        swatches.forEach((swatch) => {
          const position = Number(swatch.dataset.optionPosition) - 1;
          const possible = variants.some((variant) => variant.available && variant.options.every((option, index) => (
            index === position ? option === swatch.value : !selected[index] || option === selected[index]
          )));
          swatch.disabled = !possible;
          const label = card.querySelector(`label[for="${CSS.escape(swatch.id)}"]`);
          if (label) label.setAttribute('aria-disabled', String(!possible));
        });
      };

      const update = () => {
        const variant = selectedVariant();
        const available = Boolean(variant?.available);
        const onSale = Number(variant?.compare_at_price || 0) > Number(variant?.price || 0);

        if (price) setMoney(price, variant?.price || 0, moneyFormat);
        if (compare) {
          compare.hidden = !onSale;
          setMoney(compare, onSale ? variant.compare_at_price : null, moneyFormat);
        }
        if (saving) {
          saving.hidden = !onSale;
          setMoney(saving, onSale ? Number(variant.compare_at_price) - Number(variant.price) : null,
            moneyFormat, onSale ? `${card.dataset.savingsPrefix} ` : '');
        }
        if (image && variant?.image && image.dataset.variantImage !== variant.image) {
          setVariantImage(image, variant.image);
        }
        swatches.forEach((swatch) => {
          const selectedValue = selections()[Number(swatch.dataset.optionPosition) - 1];
          swatch.checked = swatch.value === selectedValue;
        });

        updateAvailability();

        if (!button) return;
        button.disabled = busy || !available;
        if (busy) return;
        button.textContent = !variant
          ? button.dataset.unavailableLabel
          : !available
            ? button.dataset.soldOutLabel
            : !defaultOnly && picker && !picker.hidden
              ? button.dataset.confirmLabel
              : button.dataset.addLabel;
      };

      swatches.forEach((swatch) => swatch.addEventListener('change', () => {
        const matchingSelect = selects.find(
          (select) => select.dataset.optionPosition === swatch.dataset.optionPosition
        );
        if (matchingSelect) matchingSelect.value = swatch.value;
        card.classList.add('has-selected-variant');
        setError();
        update();
      }));

      selects.forEach((select) => select.addEventListener('change', () => {
        card.classList.add('has-selected-variant');
        setError();
        update();
      }));

      button?.addEventListener('click', async () => {
        if (!defaultOnly && picker?.hidden) {
          picker.hidden = false;
          button.setAttribute('aria-expanded', 'true');
          setError();
          update();
          selects[0]?.focus();
          return;
        }

        const variant = selectedVariant();
        if (!variant?.available || busy) {
          setError(card.dataset.errorMessage);
          return;
        }

        busy = true;
        setError();
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel;

        try {
          await addCartItems([{
            id: variant.id,
            quantity: 1,
            properties: { _editorial_offer: 'you-may-also-like' }
          }]);
          if (picker) picker.hidden = true;
          button.setAttribute('aria-expanded', 'false');
        } catch (error) {
          setError(error.message || card.dataset.errorMessage);
        } finally {
          busy = false;
          button.removeAttribute('aria-busy');
          update();
        }
      });

      update();
    });
  };

  const initProduct = (root) => {
    const data = root.querySelector('[data-epp-product-json]');
    const form = root.querySelector('[data-epp-product-form]');
    if (!data || !form) return;

    let variants;
    try {
      variants = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }

    const radios = [...root.querySelectorAll('[data-epp-option-input]')];
    const selects = [...root.querySelectorAll('[data-epp-option-select]')];
    const idInput = form.querySelector('[data-epp-variant-id]');
    const price = root.querySelector('[data-epp-price]');
    const compare = root.querySelector('[data-epp-compare]');
    const saving = root.querySelector('[data-epp-saving]');
    const stock = root.querySelector('[data-epp-stock]');
    const stockText = root.querySelector('[data-epp-stock-text]');
    const addButton = form.querySelector('[data-epp-add]');
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const showButtonPrice = root.dataset.showButtonPrice === 'true';
    const updateUrl = root.dataset.updateUrl === 'true';

    const selections = () => {
      const selected = [];
      selects.forEach((select) => {
        selected[Number(select.dataset.optionPosition) - 1] = select.value;
      });
      radios.filter((radio) => radio.checked).forEach((radio) => {
        selected[Number(radio.dataset.optionPosition) - 1] = radio.value;
      });
      return selected;
    };

    const variantFor = (selected) => variants.find((variant) => (
      variant.options.length === selected.length &&
      variant.options.every((option, index) => option === selected[index])
    ));

    const updateLabels = () => {
      selections().forEach((value, index) => {
        root.querySelectorAll(`[data-epp-selected-option="${index + 1}"]`).forEach((label) => {
          label.textContent = value || '';
        });
      });
    };

    const updateAvailability = () => {
      const selected = selections();
      radios.forEach((radio) => {
        const position = Number(radio.dataset.optionPosition) - 1;
        const possible = variants.some((variant) => variant.available && variant.options.every((option, index) => (
          index === position ? option === radio.value : !selected[index] || option === selected[index]
        )));
        radio.disabled = !possible;
        const label = root.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        if (label) label.setAttribute('aria-disabled', String(!possible));
      });
      selects.forEach((select) => {
        const position = Number(select.dataset.optionPosition) - 1;
        [...select.options].forEach((optionElement) => {
          const possible = variants.some((variant) => variant.available && variant.options.every((option, index) => (
            index === position ? option === optionElement.value : !selected[index] || option === selected[index]
          )));
          optionElement.disabled = !possible && optionElement.value !== select.value;
        });
      });
    };

    const updatePrice = (variant) => {
      if (!variant) return;
      if (price) setMoney(price, variant.price, moneyFormat);
      const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      if (compare) {
        compare.hidden = !onSale;
        setMoney(compare, onSale ? variant.compare_at_price : null, moneyFormat);
      }
      if (saving) {
        saving.hidden = !onSale;
        if (onSale) {
          const percent = Math.round((1 - Number(variant.price) / Number(variant.compare_at_price)) * 100);
          saving.textContent = replaceTokens(saving.dataset.template, { savings: percent });
        }
      }
    };

    const updateStock = (variant) => {
      if (!stock || !stockText) return;
      if (!variant) {
        stock.hidden = false;
        stockText.textContent = stock.dataset.unavailableMessage;
        return;
      }

      const quantity = Number(variant.inventory_quantity);
      const threshold = Number(stock.dataset.threshold || 0);
      const policy = variant.inventory_policy;
      let template = '';

      if (!variant.available) {
        template = stock.dataset.outMessage;
      } else if (Number.isFinite(quantity) && quantity <= 0 && policy === 'continue') {
        template = stock.dataset.continueMessage;
      } else if (Number.isFinite(quantity) && quantity > 0 && quantity <= threshold) {
        template = stock.dataset.lowMessage;
      } else if (stock.dataset.showInStock === 'true') {
        template = stock.dataset.inMessage;
      }

      stock.hidden = !template;
      stockText.textContent = replaceTokens(template, {
        inventory: Number.isFinite(quantity) ? Math.max(0, quantity) : '',
        variant: variant.title || ''
      });
    };

    const updateButton = (variant) => {
      if (!addButton) return;
      const available = Boolean(variant && variant.available);
      addButton.disabled = !available;
      const label = !variant
        ? addButton.dataset.unavailableLabel
        : available
          ? addButton.dataset.availableLabel
          : addButton.dataset.soldOutLabel;
      setMoney(addButton, available && showButtonPrice ? variant.price : null, moneyFormat,
        available && showButtonPrice ? `${label} · ` : label);
    };

    let firstUpdate = true;
    const updateVariant = () => {
      const variant = variantFor(selections());
      if (idInput) idInput.value = variant ? variant.id : '';
      updateLabels();
      updateAvailability();
      updatePrice(variant);
      updateStock(variant);
      updateButton(variant);
      if (variant?.featured_media_id) {
        promoteMedia(root, variant.featured_media_id, firstUpdate ? 'auto' : 'smooth');
      }
      firstUpdate = false;

      if (variant && updateUrl && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      root.dispatchEvent(new CustomEvent('editorial:variant-change', {
        bubbles: true,
        detail: { variant }
      }));
    };

    radios.forEach((radio) => radio.addEventListener('change', updateVariant));
    selects.forEach((select) => select.addEventListener('change', updateVariant));
    updateVariant();
  };

  const initRoot = (root) => {
    if (!root || root.dataset.eppBound === 'true') return;
    root.dataset.eppBound = 'true';
    initGallery(root);
    initModal(root);
    initQuantity(root);
    initCartDrawer(root);
    initBundle(root);
    initPairs(root);
    initRecommendations(root);
    initProduct(root);
  };

  const initAll = (scope = document) => {
    syncPageContainment();
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', () => {
    window.requestAnimationFrame(syncPageContainment);
  });
  window.addEventListener('scroll', pinPageInlineOrigin, { passive: true });
  window.addEventListener('pageshow', pinPageInlineOrigin);
})();


